import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, AlertCircle, Mail, Users, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { 
  getInvitationByToken, 
  acceptInvitation, 
  declineInvitation,
  type ProjectInvitation 
} from '../../projects/lib/projectSharing';

export function InvitationResponsePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invitation, setInvitation] = useState<ProjectInvitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (token) {
      loadInvitation();
    }
  }, [token]);

  const loadInvitation = async () => {
    if (!token) return;

    try {
      const invitationData = await getInvitationByToken(token);
      if (!invitationData) {
        setError('Invitation not found or has expired');
      } else {
        setInvitation(invitationData);
      }
    } catch (error) {
      console.error('Error loading invitation:', error);
      setError('Failed to load invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!invitation || !user) return;

    // Check if user email matches invitation email
    if (user.email !== invitation.invited_email) {
      toast.error('You can only accept invitations sent to your email address');
      return;
    }

    setResponding(true);
    try {
      const success = await acceptInvitation(invitation.id);
      if (success) {
        toast.success('Invitation accepted! You now have access to the project.');
        navigate('/projects');
      } else {
        toast.error('Failed to accept invitation');
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      toast.error('Failed to accept invitation');
    } finally {
      setResponding(false);
    }
  };

  const handleDecline = async () => {
    if (!invitation || !user) return;

    // Check if user email matches invitation email
    if (user.email !== invitation.invited_email) {
      toast.error('You can only decline invitations sent to your email address');
      return;
    }

    if (!confirm('Are you sure you want to decline this invitation?')) {
      return;
    }

    setResponding(true);
    try {
      const success = await declineInvitation(invitation.id);
      if (success) {
        toast.success('Invitation declined');
        navigate('/projects');
      } else {
        toast.error('Failed to decline invitation');
      }
    } catch (error) {
      console.error('Error declining invitation:', error);
      toast.error('Failed to decline invitation');
    } finally {
      setResponding(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error || !invitation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-sm shadow-md p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Invitation Not Found
          </h2>
          <p className="text-gray-600 mb-6">
            {error || 'This invitation may have expired or been cancelled.'}
          </p>
          <button
            onClick={() => navigate('/projects')}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Go to Projects
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-sm shadow-md p-6 text-center">
          <Mail className="h-12 w-12 text-blue-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Sign In Required
          </h2>
          <p className="text-gray-600 mb-6">
            Please sign in to respond to this project invitation.
          </p>
          <button
            onClick={() => navigate('/auth')}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  const isExpired = new Date(invitation.expires_at) < new Date();
  const canRespond = user.email === invitation.invited_email && !isExpired;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-sm shadow-md overflow-hidden">
        {/* Header */}
        <div className="bg-blue-600 text-white p-6">
          <div className="flex items-center gap-3">
            <Users className="h-8 w-8" />
            <div>
              <h1 className="text-2xl font-bold">Project Invitation</h1>
              <p className="text-blue-100">You've been invited to collaborate</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Invitation Details */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {invitation.project_name}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-sm">
                <Mail className="h-5 w-5 text-gray-500" />
                <div>
                  <p className="text-sm text-gray-500">From</p>
                  <p className="font-medium text-gray-900">{invitation.owner_email}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-sm">
                <Users className="h-5 w-5 text-gray-500" />
                <div>
                  <p className="text-sm text-gray-500">Permission Level</p>
                  <p className="font-medium text-gray-900 capitalize">
                    {invitation.permission_level}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-sm">
                <Calendar className="h-5 w-5 text-gray-500" />
                <div>
                  <p className="text-sm text-gray-500">Sent</p>
                  <p className="font-medium text-gray-900">
                    {new Date(invitation.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-sm">
                <Clock className="h-5 w-5 text-gray-500" />
                <div>
                  <p className="text-sm text-gray-500">Expires</p>
                  <p className="font-medium text-gray-900">
                    {new Date(invitation.expires_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Permission Description */}
            <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
              <h3 className="font-medium text-blue-900 mb-2">
                {invitation.permission_level === 'view' ? 'View' : 'Edit'} Access
              </h3>
              <p className="text-blue-800 text-sm">
                {invitation.permission_level === 'view' 
                  ? 'You will be able to view the project and its jobs in read-only mode.'
                  : 'You will be able to view and modify the project and create new jobs.'
                }
              </p>
            </div>
          </div>

          {/* Status Messages */}
          {isExpired && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-sm">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-500" />
                <p className="text-red-800 font-medium">This invitation has expired</p>
              </div>
              <p className="text-red-700 text-sm mt-1">
                Please contact the project owner for a new invitation.
              </p>
            </div>
          )}

          {user.email !== invitation.invited_email && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-sm">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-yellow-500" />
                <p className="text-yellow-800 font-medium">Email mismatch</p>
              </div>
              <p className="text-yellow-700 text-sm mt-1">
                This invitation was sent to {invitation.invited_email}, but you're signed in as {user.email}.
              </p>
            </div>
          )}

          {/* Action Buttons */}
          {canRespond ? (
            <div className="flex gap-4">
              <button
                onClick={handleAccept}
                disabled={responding}
                className="flex-1 bg-green-600 text-white py-3 px-6 rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {responding ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  <CheckCircle className="h-5 w-5" />
                )}
                Accept Invitation
              </button>
              
              <button
                onClick={handleDecline}
                disabled={responding}
                className="flex-1 bg-red-600 text-white py-3 px-6 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {responding ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  <XCircle className="h-5 w-5" />
                )}
                Decline Invitation
              </button>
            </div>
          ) : (
            <button
              onClick={() => navigate('/projects')}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-md hover:bg-blue-700 transition-colors"
            >
              Go to Projects
            </button>
          )}
        </div>
      </div>
    </div>
  );
}